import { useMemo, useState, type FormEvent } from "react";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { API_BASE_URL } from "./api/client";
import { useAsync } from "./hooks/useAsync";
import type { BrandListResponse } from "./api/types";
import { MarketingMetrics } from "./tabs/MarketingMetrics";
import { AdPerformance } from "./tabs/AdPerformance";
import { AssetLibrary } from "./tabs/AssetLibrary";

type TabId = "metrics" | "performance" | "assets";

const TABS: { id: TabId; label: string }[] = [
  { id: "metrics", label: "Marketing Metrics" },
  { id: "performance", label: "Ad Performance" },
  { id: "assets", label: "Asset Library" },
];

const BRAND_STORAGE_KEY = "gaiiff.dashboard.brandId";

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "Something went wrong. Please try again.";
}

// -----------------------------------------------------------------------------
// Login screen — email/password sign-in plus NEW_PASSWORD_REQUIRED challenge.
// -----------------------------------------------------------------------------

function Login() {
  const { signIn, completeNewPassword } = useAuth();

  const [phase, setPhase] = useState<"signIn" | "newPassword">("signIn");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSignIn(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await signIn(email, password);
      if (result.status === "newPasswordRequired") {
        setPhase("newPassword");
      }
      // On success, AuthProvider flips isAuthenticated and this screen unmounts.
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await completeNewPassword(newPassword);
      // On success, AuthProvider flips isAuthenticated and this screen unmounts.
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <h1 className="login__title">GAIIFF Marketing Dashboard</h1>

        {phase === "signIn" ? (
          <form className="login__form" onSubmit={handleSignIn} noValidate>
            <p className="section-note">
              Sign in with your account to continue.
            </p>

            <label className="field">
              <span>Email</span>
              <input
                type="email"
                name="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </label>

            <label className="field">
              <span>Password</span>
              <input
                type="password"
                name="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </label>

            {error && (
              <p className="form-err" role="alert">
                {error}
              </p>
            )}

            <button type="submit" disabled={loading} className="login__submit">
              {loading && <span className="spinner" aria-hidden="true" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        ) : (
          <form className="login__form" onSubmit={handleNewPassword} noValidate>
            <p className="section-note">
              Your account requires a new password. Choose one to finish signing
              in.
            </p>

            <label className="field">
              <span>New password</span>
              <input
                type="password"
                name="new-password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoFocus
              />
            </label>

            <label className="field">
              <span>Confirm new password</span>
              <input
                type="password"
                name="confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
            </label>

            {error && (
              <p className="form-err" role="alert">
                {error}
              </p>
            )}

            <button type="submit" disabled={loading} className="login__submit">
              {loading && <span className="spinner" aria-hidden="true" />}
              {loading ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Authenticated shell — the three tabs plus sign-out.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// Brand picker — a dropdown of the brands managed by the CMS. Falls back to a
// free-text id field if the brand list cannot be loaded, so the dashboard stays
// usable even when the /api/brands endpoint is unavailable.
// -----------------------------------------------------------------------------

function BrandPicker({
  brandId,
  onChange,
}: {
  brandId: string;
  onChange: (next: string) => void;
}) {
  const { api } = useAuth();
  const { data, loading, error } = useAsync<BrandListResponse>(
    (signal) => api.listBrands(signal),
    [],
  );

  const brands = data?.brands ?? [];

  // If the list failed to load and we have nothing to show, degrade to the
  // original free-text input rather than trapping the user with an empty menu.
  if (error && brands.length === 0) {
    return (
      <label className="brand-field">
        Brand ID
        <input
          type="text"
          value={brandId}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. brand-123"
          aria-label="Brand ID"
        />
        <span className="brand-field__hint" title={error}>
          Couldn’t load brands — enter an ID manually.
        </span>
      </label>
    );
  }

  // Preserve a stored brand id that isn't (yet) in the fetched list so the
  // current selection is never silently dropped.
  const hasStored = brandId.length > 0 && !brands.some((b) => b.id === brandId);

  return (
    <label className="brand-field">
      Brand
      <select
        value={brandId}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Brand"
        disabled={loading}
      >
        <option value="">
          {loading ? "Loading brands…" : "Select a brand…"}
        </option>
        {brands.map((b) => (
          <option key={b.id} value={b.id}>
            {b.displayName}
          </option>
        ))}
        {hasStored && <option value={brandId}>{brandId}</option>}
      </select>
    </label>
  );
}

function Shell() {
  const { email, signOut } = useAuth();
  const [active, setActive] = useState<TabId>("metrics");
  const [brandId, setBrandId] = useState<string>(
    () => window.localStorage.getItem(BRAND_STORAGE_KEY) ?? "",
  );

  function updateBrand(next: string) {
    setBrandId(next);
    try {
      window.localStorage.setItem(BRAND_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }

  const activePanel = useMemo(() => {
    switch (active) {
      case "metrics":
        return <MarketingMetrics brandId={brandId} />;
      case "performance":
        return <AdPerformance brandId={brandId} />;
      case "assets":
        return <AssetLibrary brandId={brandId} />;
    }
  }, [active, brandId]);

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header__title">
          <h1>GAIIFF Marketing Dashboard</h1>
          <span className="api-base" title={API_BASE_URL}>
            API: {API_BASE_URL.replace(/^https?:\/\//, "")}
          </span>
        </div>
        <div className="app-header__controls">
          <BrandPicker brandId={brandId} onChange={updateBrand} />
          <div className="account">
            {email && (
              <span className="account__email" title={email}>
                {email}
              </span>
            )}
            <button
              type="button"
              className="account__signout"
              onClick={signOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <nav className="tabs" role="tablist" aria-label="Dashboard sections">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={active === tab.id}
            aria-controls={`panel-${tab.id}`}
            className={active === tab.id ? "tab tab--active" : "tab"}
            onClick={() => setActive(tab.id)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <main
        className="panel"
        role="tabpanel"
        id={`panel-${active}`}
        aria-labelledby={`tab-${active}`}
      >
        {activePanel}
      </main>
    </div>
  );
}

function Gate() {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <Shell /> : <Login />;
}

export function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}
